<template>
  <div>
    <div class="layer-toggle-container">
      <x-img :src="WIND_LAYER_INFO.img" data-test="layer-thumbnail" />
      <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
      <label :for="WIND_LAYER_INFO.id" data-test="layer-label">
        <ys-text type="title5">{{ WIND_LAYER_INFO.label }}</ys-text>
        <ys-text type="body2">{{ WIND_LAYER_INFO.byline }}</ys-text>
      </label>
      <ys-switch
        v-if="hasWindAccess"
        :id="WIND_LAYER_INFO.id"
        :selected="windState"
        :data-test="`${WIND_LAYER_INFO.id}-toggle`"
        @change="toggleLayer"
      />
      <span v-else class="lock">
        <x-icon
          icon="lock"
          color="white"
          width="15"
          height="15"
          data-test="lock"
        />
      </span>
    </div>
    <hr />
    <button class="more-details-container" @click="$emit('open-layer-details')">
      <ys-text type="body1">Layer Details</ys-text>
      <ys-icon icon="ysChevronRight" size="small" />
    </button>
  </div>
</template>
<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/icons/ysChevronRight'
import '@onxmaps/yellowstone-ui/ys-switch'
import type { YsSwitch } from '@onxmaps/yellowstone-ui/ys-switch'

import xIcon from '@/components/shared/x-icon/index.vue'
import xImg from '@/components/shared/x-img/index.vue'
import { setWindLayer } from '@/fish/components/ui/wind/utils'
import { WIND_LAYER_INFO } from '@/fish/components/map-controls/utils'

import marketingEventService from '@/services/marketing-event-service'
import { basemapMarketingName } from '@/services/basemap-service'
import { useHasAccess } from '@/services/feature-access/composables'
import { FishGates } from '@/services/feature-access/fish/types'

import { CARD_ORIGINS } from '@/lib/enums-marketing'
import { getMode } from '@/lib/modes'

import { getItemRef, saveItem } from '@/plugins/storage'
import { injectXWeather } from '@/fish/providers/x-weather'

///// types /////
interface Props {
  eventOrigin?: string
}
///// props/emits /////
const props = withDefaults(defineProps<Props>(), {
  eventOrigin: CARD_ORIGINS.BASEMAP,
})

defineEmits(['open-layer-details'])

///// refs and variables /////
const xWeather = injectXWeather()
const windState = getItemRef<boolean>('wind-forecast-enabled')
const { hasAccess: hasWindAccess } = useHasAccess(FishGates.animated_wind)

///// computed /////
const mapMode = computed(getMode)

///// functions /////
const toggleLayer = (event: Event) => {
  const toggleState = (event.target as YsSwitch).selected
  saveItem('wind-forecast-enabled', toggleState)
  const layerState = toggleState ? 'enabled' : 'disabled'

  marketingEventService.publish(`layer_${layerState}`, {
    name: `layer_${layerState}`,
    origin: props.eventOrigin,
    layer_name: WIND_LAYER_INFO.label,
    map_mode: mapMode.value,
    active_basemap: basemapMarketingName.value,
  })

  setWindLayer(toggleState, xWeather)
}
</script>
<style scoped>
.layer-toggle-container {
  display: flex;
  justify-content: space-between;
  gap: 16px;

  label {
    flex: 1;
  }

  h5 {
    margin-bottom: 3px;
  }

  img {
    width: 48px;
    height: 48px;
    border-radius: 8px;
  }

  .lock {
    border-radius: 50%;
    min-width: 30px;
    height: 30px;
    background-color: var(--ys-color-icon-twotone-primary);
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

hr {
  margin: 8px 0px 10px 64px;
}

.more-details-container {
  all: unset;
  width: -webkit-fill-available;
  margin-left: 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}
</style>
